import { useEffect, useRef, useState } from 'react';
import { BarChart3, Check, Database, Palette, Sliders, Wand2, X } from 'lucide-react';
import type {
  Aggregation,
  CustomModelSchema,
  CustomQuery,
  DateInterval,
  DomainRule,
  MetricKey,
  SortDir,
  Visualization,
  WidgetConfig,
} from '@contracts';
import { METRIC_CATALOG } from '@contracts';
import { isAdmin } from '@/services/authService';
import { CATALOG_AS_CUSTOM, hasCustomPreset } from '@/lib/catalogPresets';
import { metricAppliesToCompanies } from '@/lib/companyVisibility';
import type { SourceConfig } from '@/services/sourceConfigService';
import { fetchCustomSchema } from '@/services/customMetricsService';
import { Preview, Section } from '@/components/wizard/WizardUI';
import { STEPS, VIZ_META } from '@/components/wizard/wizardMeta';
import { CustomMetricStep } from '@/components/wizard/CustomMetricStep';
import { CustomDetailStep } from '@/components/wizard/CustomDetailStep';

export function WidgetWizard({
  initial,
  companies,
  sourceOverrides,
  onSave,
  onClose,
}: {
  initial: WidgetConfig | null;
  companies: string[];
  sourceOverrides: SourceConfig;
  onSave: (config: WidgetConfig) => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState<'catalog' | 'custom'>(initial?.customQuery ? 'custom' : 'catalog');
  const [metric, setMetric] = useState<MetricKey | null>(initial?.metric ?? null);
  const [visualization, setVisualization] = useState<Visualization>(initial?.visualization ?? 'kpi');
  // Al EDITAR, el campo de nombre trae el nombre actual: el título propio o, si no
  // tiene, la etiqueta del catálogo (para custom se rellena al cargar el schema).
  const [title, setTitle] = useState(
    initial?.title ?? (initial?.metric ? METRIC_CATALOG.find((m) => m.key === initial.metric)?.label : '') ?? '',
  );
  const titleSeeded = useRef(false);

  // Estado del builder personalizado
  const [schema, setSchema] = useState<CustomModelSchema[]>([]);
  const cq = initial?.customQuery;
  const [cModel, setCModel] = useState(cq?.model ?? '');
  const [cAgg, setCAgg] = useState<Aggregation>(cq?.aggregation ?? 'count');
  const [cField, setCField] = useState(cq?.field ?? '');
  const [cGroupBy, setCGroupBy] = useState(cq?.groupBy ?? '');
  const [cInterval, setCInterval] = useState<DateInterval | ''>(cq?.groupByInterval ?? '');
  const [cDomain, setCDomain] = useState<DomainRule[]>(cq?.domain ?? []);
  const [cMasterRules, setCMasterRules] = useState<string[]>(cq?.masterRules ?? []);
  const [usePeriod, setUsePeriod] = useState(!!cq?.dateField);
  // Pestaña del builder custom: Cálculo (afuera) | Detalle (lo que se ve al abrir).
  const [customTab, setCustomTab] = useState<'calc' | 'detail'>('calc');
  const [cDetailCols, setCDetailCols] = useState<string[]>(cq?.detail?.columns ?? []);
  const [cDetailSortBy, setCDetailSortBy] = useState(cq?.detail?.sortBy ?? '');
  const [cDetailSortDir, setCDetailSortDir] = useState<SortDir>(cq?.detail?.sortDir ?? 'desc');
  const [cDetailLimit, setCDetailLimit] = useState<number | ''>(cq?.detail?.limit ?? '');
  const [cDetailGroupBy, setCDetailGroupBy] = useState(cq?.detail?.groupBy ?? '');
  const [cDetailExtra, setCDetailExtra] = useState<DomainRule[]>(cq?.detail?.extraDomain ?? []);

  // Editar un widget custom sin nombre propio → prellenar con la etiqueta del modelo.
  useEffect(() => {
    if (!titleSeeded.current && initial && !initial.title && initial.customQuery) {
      const lbl = schema.find((s) => s.model === initial.customQuery?.model)?.label;
      if (lbl) {
        setTitle(lbl);
        titleSeeded.current = true;
      }
    }
  }, [schema, initial]);

  useEffect(() => {
    if (mode === 'custom' && schema.length === 0) {
      fetchCustomSchema().then(setSchema).catch(() => setSchema([]));
    }
  }, [mode, schema.length]);

  const entry = METRIC_CATALOG.find((m) => m.key === metric);
  const modelSchema = schema.find((s) => s.model === cModel);
  const numericFields = modelSchema?.fields.filter((f) => f.type === 'number') ?? [];
  const isDateField = (name: string) => !!modelSchema?.dateFields.includes(name);

  const vizOptions: Visualization[] =
    mode === 'custom'
      ? cInterval
        ? ['area', 'line']
        : cGroupBy
          ? ['bar', 'table']
          : ['kpi']
      : (entry?.supportedVisualizations ?? ['kpi']);

  const pickMetric = (k: MetricKey) => {
    setMetric(k);
    const e = METRIC_CATALOG.find((m) => m.key === k);
    if (e && !e.supportedVisualizations.includes(visualization)) setVisualization(e.defaultVisualization);
  };

  const pickModel = (model: string) => {
    setCModel(model);
    setCField('');
    setCGroupBy('');
    setCInterval('');
    setCDomain([]);
    setCMasterRules([]); // las reglas son por-modelo: limpiar al cambiar de fuente
    setCDetailCols([]); // columnas/orden/filtros del detalle también son por-modelo
    setCDetailSortBy('');
    setCDetailGroupBy('');
    setCDetailExtra([]);
  };

  const toggleMasterRule = (id: string) =>
    setCMasterRules((rs) => (rs.includes(id) ? rs.filter((r) => r !== id) : [...rs, id]));

  const setGroup = (g: string) => {
    setCGroupBy(g);
    if (g && isDateField(g)) {
      setCInterval('month');
      setVisualization('area');
    } else {
      setCInterval('');
      setVisualization(g ? 'bar' : 'kpi');
    }
  };

  const pickInterval = (iv: DateInterval | '') => {
    setCInterval(iv);
    setVisualization(iv ? 'area' : 'bar');
  };

  const base = {
    view: initial?.view ?? ('ventas' as const),
    period: initial?.period ?? ('last_30d' as const),
    title: title.trim() || undefined,
  };

  // Estado actual del builder como CustomQuery (null si incompleto). Fuente única
  // tanto para guardar el widget como para guardar la plantilla reutilizable.
  const currentQuery: CustomQuery | null =
    mode === 'custom' && cModel && (cAgg === 'count' || !!cField)
      ? {
          model: cModel,
          aggregation: cAgg,
          field: cAgg === 'count' ? undefined : cField,
          domain: cDomain,
          masterRules: cMasterRules.length ? cMasterRules : undefined,
          groupBy: cGroupBy || undefined,
          groupByInterval: cInterval || undefined,
          groupLimit: 20,
          visualization,
          dateField: usePeriod ? modelSchema?.dateFields[0] : undefined,
          detail:
            cDetailCols.length || cDetailSortBy || cDetailLimit || cDetailGroupBy || cDetailExtra.length
              ? {
                  columns: cDetailCols.length ? cDetailCols : undefined,
                  sortBy: cDetailSortBy || undefined,
                  sortDir: cDetailSortDir,
                  limit: cDetailLimit || undefined,
                  groupBy: cDetailGroupBy || undefined,
                  extraDomain: cDetailExtra.length ? cDetailExtra : undefined,
                }
              : undefined,
        }
      : null;

  // Cargar una plantilla guardada → prellenar todo el builder.
  const applyQuery = (q: CustomQuery) => {
    setCModel(q.model);
    setCAgg(q.aggregation);
    setCField(q.field ?? '');
    setCGroupBy(q.groupBy ?? '');
    setCInterval(q.groupByInterval ?? '');
    setCDomain(q.domain ?? []);
    setCMasterRules(q.masterRules ?? []);
    setUsePeriod(!!q.dateField);
    setVisualization(q.visualization);
    setCDetailCols(q.detail?.columns ?? []);
    setCDetailSortBy(q.detail?.sortBy ?? '');
    setCDetailSortDir(q.detail?.sortDir ?? 'desc');
    setCDetailLimit(q.detail?.limit ?? '');
    setCDetailGroupBy(q.detail?.groupBy ?? '');
    setCDetailExtra(q.detail?.extraDomain ?? []);
  };

  // "Personalizar" una métrica de catálogo → carga su equivalente custom (modelo +
  // filtros + reglas precargados) y pasa al builder, para verla/editarla.
  const forkToCustom = (m: MetricKey) => {
    const preset = CATALOG_AS_CUSTOM[m];
    if (!preset) return;
    const e = METRIC_CATALOG.find((x) => x.key === m);
    if (!title.trim() && e) setTitle(e.label);
    applyQuery(preset);
    setMode('custom');
  };

  const save = () => {
    // Conservar la config original (appearance/scope/comparePeriod/refreshSec) al
    // editar; el wizard solo sobrescribe lo que toca.
    const prev = initial ?? {};
    if (mode === 'custom') {
      if (!currentQuery) return;
      onSave({ ...prev, metric: null, customQuery: currentQuery, visualization, ...base });
      return;
    }
    if (!metric) return;
    onSave({ ...prev, metric, customQuery: undefined, visualization, ...base });
  };

  const canNext =
    step !== 0 ? true : mode === 'custom' ? !!cModel && (cAgg === 'count' || !!cField) : !!metric;

  const titlePlaceholder = mode === 'custom' ? modelSchema?.label ?? 'Mi métrica' : entry?.label ?? 'Mi widget';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex max-h-[88dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-red-50 text-red-600">
              <Sliders className="h-5 w-5" />
            </span>
            <h2 className="text-lg font-bold text-slate-900">{initial ? 'Editar widget' : 'Nuevo widget'}</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center px-6 py-5">
          {STEPS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center last:flex-none">
              <button onClick={() => i < step && setStep(i)} className="flex items-center gap-2">
                <span
                  className={`grid h-8 w-8 place-items-center rounded-full text-sm font-bold transition ${
                    i < step
                      ? 'bg-red-600 text-white'
                      : i === step
                        ? 'border-2 border-red-600 text-red-600'
                        : 'border-2 border-slate-200 text-slate-300'
                  }`}
                >
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`text-sm font-semibold ${i <= step ? 'text-slate-800' : 'text-slate-300'}`}>{label}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`mx-3 h-0.5 flex-1 ${i < step ? 'bg-red-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-auto px-6 pb-2">
          {step === 0 && mode === 'catalog' && (
            <Section icon={<Database className="h-4 w-4 text-red-600" />} title="Fuente de datos">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {METRIC_CATALOG.filter((m) => metricAppliesToCompanies(m, companies, sourceOverrides)).map((m) => (
                  <button
                    key={m.key}
                    onClick={() => (isAdmin() && hasCustomPreset(m.key) ? forkToCustom(m.key) : pickMetric(m.key))}
                    className={`rounded-xl border p-3 text-left transition ${
                      metric === m.key
                        ? 'border-red-600 bg-red-50 ring-1 ring-red-600'
                        : 'border-slate-200 hover:border-red-300 hover:bg-slate-50'
                    }`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{m.label}</p>
                    {isAdmin() && hasCustomPreset(m.key) ? (
                      <p className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-red-500">
                        <Sliders className="h-2.5 w-2.5" /> personalizable
                      </p>
                    ) : (
                      m.affectedByView && <p className="mt-0.5 text-[10px] text-slate-400">cambia por vista</p>
                    )}
                  </button>
                ))}
              </div>
              {isAdmin() && (
                <button
                  onClick={() => {
                    setMode('custom');
                    setVisualization('kpi');
                  }}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-red-300 p-3 text-sm font-semibold text-red-600 hover:bg-red-50"
                >
                  <Wand2 className="h-4 w-4" /> Métrica personalizada (avanzado)
                </button>
              )}
            </Section>
          )}

          {step === 0 && mode === 'custom' && (
            <>
              {cModel && !cGroupBy && (
                <div className="mb-3 flex gap-1 rounded-xl bg-slate-100 p-1">
                  {([['calc', 'Cálculo'], ['detail', 'Detalle']] as const).map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setCustomTab(k)}
                      className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                        customTab === k ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {customTab === 'detail' && cModel && !cGroupBy ? (
                <Section icon={<Sliders className="h-4 w-4 text-red-600" />} title="Detalle (lo que se ve al abrir)">
                  <CustomDetailStep
                    fields={modelSchema?.fields ?? []}
                    columns={cDetailCols}
                    sortBy={cDetailSortBy}
                    sortDir={cDetailSortDir}
                    limit={cDetailLimit}
                    groupBy={cDetailGroupBy}
                    extraDomain={cDetailExtra}
                    onColumnsChange={setCDetailCols}
                    onSortByChange={setCDetailSortBy}
                    onSortDirChange={setCDetailSortDir}
                    onLimitChange={setCDetailLimit}
                    onGroupByChange={setCDetailGroupBy}
                    onExtraDomainChange={setCDetailExtra}
                  />
                </Section>
              ) : (
                <CustomMetricStep
                  schema={schema}
                  modelSchema={modelSchema}
                  numericFields={numericFields}
                  cModel={cModel}
                  cAgg={cAgg}
                  cField={cField}
                  cGroupBy={cGroupBy}
                  cInterval={cInterval}
                  cDomain={cDomain}
                  cMasterRules={cMasterRules}
                  usePeriod={usePeriod}
                  currentQuery={currentQuery}
                  isDateField={isDateField}
                  onBack={() => setMode('catalog')}
                  onApplyTemplate={applyQuery}
                  onPickModel={pickModel}
                  onPickAgg={setCAgg}
                  onPickField={setCField}
                  onPickGroup={setGroup}
                  onPickInterval={pickInterval}
                  onDomainChange={setCDomain}
                  onToggleMasterRule={toggleMasterRule}
                  onUsePeriodChange={setUsePeriod}
                />
              )}
            </>
          )}

          {step === 1 && (
            <Section icon={<BarChart3 className="h-4 w-4 text-red-600" />} title="Tipo de visualización">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {vizOptions.map((v) => {
                  const { label, Icon } = VIZ_META[v];
                  const active = visualization === v;
                  return (
                    <button
                      key={v}
                      onClick={() => setVisualization(v)}
                      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition ${
                        active ? 'border-red-600 bg-red-50' : 'border-slate-200 hover:border-red-300'
                      }`}
                    >
                      <Icon className={`h-6 w-6 ${active ? 'text-red-600' : 'text-slate-500'}`} />
                      <span className={`text-sm font-semibold ${active ? 'text-red-700' : 'text-slate-600'}`}>{label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Vista previa</p>
              <Preview metric={metric} visualization={visualization} />
            </Section>
          )}

          {step === 2 && (
            <Section icon={<Palette className="h-4 w-4 text-red-600" />} title="Apariencia">
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">Nombre del widget</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={`Ej: ${titlePlaceholder}`}
                className="mb-5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600"
              />
              <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                <p className="font-semibold text-slate-700">{title || titlePlaceholder}</p>
                <p className="mt-1 text-xs">
                  {mode === 'custom' ? `${modelSchema?.label ?? ''} · ${cAgg}` : entry?.label} ·{' '}
                  {VIZ_META[visualization].label}. El tamaño se ajusta arrastrando el widget en la grid.
                </p>
              </div>
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className={`rounded-xl px-4 py-2 text-sm font-semibold ${
              step === 0 ? 'invisible' : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            ← Anterior
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100">
              Cancelar
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                className="rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
              >
                Siguiente →
              </button>
            ) : (
              <button onClick={save} className="rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700">
                ✓ Guardar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
